import { parseEntityRef } from "@backstage/catalog-model";
import { Link } from "@backstage/core-components";
import { Button as BuiButton, Link as BuiLink, Text } from "@backstage/ui";
import {
	daysIdle,
	isHoldActive,
	type BundleInventoryItem,
} from "@internal/backstage-plugin-avd-common";
import { Chip, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import type { SvgIconComponent } from "@material-ui/icons";
import { ReactNode } from "react";

import type { BundleInventoryDescriptor } from "./bundleDescriptors";
import { deriveHealth, type HealthLevel } from "./bundleHealth";

export const useCellStyles = makeStyles((theme) => ({
	iconCell: { display: "inline-flex", alignItems: "center", gap: theme.spacing(0.5) },
	icon: { display: "inline-flex", cursor: "help" },
	ok: { color: theme.palette.status.ok },
	warning: { color: theme.palette.status.warning },
	error: { color: theme.palette.status.error },
	pending: { color: theme.palette.status.pending },
	aborted: { color: theme.palette.status.aborted },
	mono: { fontFamily: "monospace", wordBreak: "break-all" },
	facts: { display: "grid", gap: theme.spacing(1), margin: 0 },
	fact: {
		display: "grid",
		gridTemplateColumns: "9rem 1fr",
		gap: theme.spacing(1),
		alignItems: "baseline",
		"& dd": { margin: 0, display: "flex", alignItems: "center", gap: theme.spacing(1) },
	},
	unknownOwner: { cursor: "help", marginLeft: theme.spacing(0.5) },
}));

export type IconTone = "ok" | "warning" | "error" | "pending" | "aborted";

/** An icon-only fact: the word is its accessible name and tooltip; `tabIndex` so focus opens it too. */
export function FactIcon(
	props: Readonly<{ icon: SvgIconComponent; tone: IconTone; label: string; tooltip?: string }>,
): ReactNode {
	const classes = useCellStyles();
	const Icon = props.icon;
	return (
		<Tooltip title={props.tooltip ? `${props.label} — ${props.tooltip}` : props.label}>
			<span
				role="img"
				aria-label={props.label}
				tabIndex={0}
				className={`${classes.icon} ${classes[props.tone]}`}
			>
				<Icon fontSize="small" />
			</span>
		</Tooltip>
	);
}

/** A coarse "how long ago"; the exact instant is always one hover away. */
export function relativeTime(iso: string, now: Date = new Date()): string {
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return "—";
	const seconds = Math.round((now.getTime() - then) / 1000);
	// Clock skew reads as "just now", never as a time in the future.
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} h ago`;
	return `${Math.floor(hours / 24)} d ago`;
}

export function absoluteTime(iso: string): string {
	const at = new Date(iso);
	return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

/** The owner as a human reads it: the UPN when there is one, otherwise the slug. */
export const ownerLabel = (item: Pick<BundleInventoryItem, "owner" | "slug">): string =>
	item.owner.upn ?? item.slug;

/** In-app path to a User entity, or `undefined` for an absent or unparseable ref. */
export function catalogPath(entityRef: string): string | undefined {
	try {
		const { kind, namespace, name } = parseEntityRef(entityRef);
		return `/catalog/${namespace.toLowerCase()}/${kind.toLowerCase()}/${name}`;
	} catch {
		return undefined;
	}
}

/** The Colleague cell: a catalog link when the owner resolves, plus a marker when there is no UPN. */
export function OwnerCell(props: Readonly<{ item: BundleInventoryItem }>): ReactNode {
	const classes = useCellStyles();
	const { item } = props;
	const label = ownerLabel(item);
	const path = item.owner.entityRef ? catalogPath(item.owner.entityRef) : undefined;
	return (
		<>
			{path ? <Link to={path}>{label}</Link> : <span>{label}</span>}
			{item.owner.upn ? null : (
				<Tooltip title="No owner UPN recorded — this row was backfilled from the estate, so its slug is all the registry has. The nightly sync fills it in once the owner resolves.">
					<sup className={classes.unknownOwner} aria-label="owner not recorded">
						?
					</sup>
				</Tooltip>
			)}
		</>
	);
}

/** Days since last use; `unknown` is not `0`, so a blind bundle never reads as a busy one. */
export function idleLabel(item: BundleInventoryItem, now: Date): string {
	const days = daysIdle(item.lastSeenAt, now, item.lastSeenSource);
	return days === undefined ? "unknown" : `${days} d`;
}

export function IdleCell(props: Readonly<{ item: BundleInventoryItem; now: Date }>): ReactNode {
	const { item, now } = props;
	const held = isHoldActive(item.holdUntil, now);
	const use =
		item.lastSeenSource === "unknown"
			? "The usage signal is not reaching the registry, so recent use cannot be seen"
			: item.lastSeenAt
				? `Last use ${absoluteTime(item.lastSeenAt)} (${item.lastSeenSource === "signal" ? "a signal" : "the creation time"})`
				: "No usage watermark yet";
	const title = held ? `${use}. Held until ${absoluteTime(item.holdUntil!)}` : use;
	// `unknown` is a chip, not a number: a blind bundle must not read as a busy one.
	const days = daysIdle(item.lastSeenAt, now, item.lastSeenSource);
	if (item.status === "absent") return <span>—</span>;
	return (
		<Tooltip title={title}>
			<span>
				{days === undefined ? (
					<Chip size="small" variant="outlined" label="unknown" />
				) : (
					idleLabel(item, now)
				)}
				{held ? " · held" : ""}
			</span>
		</Tooltip>
	);
}

/** `—` for a removed desktop, `active`, or `expiring` with the warning stage and the reclaim date when there is one. */
export function LifecycleCell(
	props: Readonly<{ item: BundleInventoryItem; now: Date }>,
): ReactNode {
	const { item, now } = props;
	if (item.status === "absent") return <span>—</span>;
	if (item.lifecycle !== "expiring") return <span>active</span>;
	const held = isHoldActive(item.holdUntil, now);
	const detail = [
		item.noticeStage ? `notice: ${item.noticeStage}` : undefined,
		item.noticeDeadline && !held ? `reclaim ${absoluteTime(item.noticeDeadline)}` : undefined,
		held ? "reclaim paused by the hold" : undefined,
	]
		.filter(Boolean)
		.join(" · ");
	return (
		<Tooltip title={detail || "Past its inactivity window"}>
			<Chip size="small" color="secondary" label="expiring" tabIndex={0} />
		</Tooltip>
	);
}

const HEALTH_TONE: Record<HealthLevel, IconTone> = {
	healthy: "ok",
	attention: "warning",
	failed: "error",
};

/** The Health cell: the level and its worst reason, every reason on hover, then the type's live state. */
export function HealthCell(
	props: Readonly<{
		item: BundleInventoryItem;
		descriptor: BundleInventoryDescriptor;
		now: Date;
	}>,
): ReactNode {
	const classes = useCellStyles();
	const { item, descriptor, now } = props;
	const health = deriveHealth(item, descriptor, now);
	const first = health.reasons.find((r) => r.level !== "info") ?? health.reasons[0];
	const word = health.level === "failed" ? "Failed" : "Attention";
	const text = !first
		? "Healthy"
		: first.level === "info"
			? first.label
			: first.label === word
				? word
				: `${word} · ${first.label}`;
	const title = health.reasons.length
		? health.reasons.map((r) => `${r.issue} — ${r.why}`).join("\n")
		: "Nothing needs a decision";
	const more = health.reasons.length - 1;
	return (
		<span className={classes.iconCell}>
			<Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{title}</span>}>
				<Chip
					size="small"
					variant="outlined"
					className={classes[HEALTH_TONE[health.level]]}
					label={text}
				/>
			</Tooltip>
			{more > 0 ? <Chip size="small" variant="outlined" label={`+${more}`} /> : null}
			{descriptor.renderState?.(item)}
		</span>
	);
}

/** The facts of one detail-sheet section: a label column beside a value column. */
export function FactList(props: Readonly<{ children: ReactNode }>): ReactNode {
	const classes = useCellStyles();
	return <dl className={classes.facts}>{props.children}</dl>;
}

/** One label/value line of a detail section; with `href` the value is a link, with `copy` it has a Copy button. */
export function DetailFact(
	props: Readonly<{
		label: string;
		value?: string;
		mono?: boolean;
		href?: string;
		copy?: boolean;
	}>,
): ReactNode {
	const classes = useCellStyles();
	const { label, value, href } = props;
	return (
		<div className={classes.fact}>
			<dt>
				<Text variant="body-small" color="secondary">
					{label}
				</Text>
			</dt>
			<dd>
				<Text variant="body-medium" className={props.mono ? classes.mono : undefined}>
					{href ? (
						<BuiLink href={href} target="_blank" rel="noopener noreferrer">
							{value ?? "Open"}
						</BuiLink>
					) : (
						(value ?? "—")
					)}
				</Text>
				{props.copy && value ? (
					<BuiButton
						size="small"
						variant="tertiary"
						aria-label={`Copy ${label}`}
						onPress={() => void navigator.clipboard?.writeText(value)}
					>
						Copy
					</BuiButton>
				) : null}
			</dd>
		</div>
	);
}
